import { useMemo, useState } from 'react'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Spinner from 'common/components/spinner'
import Table, { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import { generateDeleteButtonColumn } from 'common/components/table/columns'
import { formatDate } from 'common/helpers/date'
import { useAppSelector } from 'common/hooks/redux'
import {
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
} from 'common/icons'

import { CustomerAttributesResponse } from '../api'
import useDeleteCustomerAttributesMutation from '../api/mutations/useDeleteCustomerAttributesMutation'
import { CustomerAttributes } from '../api/queries/useGetCustomerAttributesQuery'

type CustomerAttributesColumn = Pick<
  CustomerAttributesResponse,
  'created' | 'fileName' | 'type'
> & { id: undefined }

type Props = {
  isLoading: boolean
  customerAttributes: CustomerAttributes
  baseUrl: string
}

const CustomerAttributesTable = ({
  baseUrl,
  isLoading,
  customerAttributes,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  const deleteQuery = useDeleteCustomerAttributesMutation(accountId)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false)

  const columns: Column<CustomerAttributesColumn>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'fileName',
        width: 600,
        minWidth: 200,
        Cell: ({ row }) => {
          return (
            <BaseCell className="bg-coolGray-50">
              <Button
                className="hover:text-orange-400 focus:outline-none focus-visible:outline-black group -ml-4 max-w-min"
                onClick={() => setIsCollapsed(!isCollapsed)}
                prefixIcon={
                  isCollapsed ? (
                    <ChevronRightIcon size={4} />
                  ) : (
                    <ChevronDownIcon isDefaultColor={false} size={4} />
                  )
                }
              >
                <span className="overflow-hidden truncate">
                  {row.original.fileName}
                </span>
              </Button>
            </BaseCell>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Type',
        accessor: 'type',
        width: 80,
        disableSortBy: true,
        Cell: ({ row }) => {
          return <BaseCell className="uppercase">{row.original.type}</BaseCell>
        },
      },
      {
        Header: 'Created',
        accessor: 'created',
        width: 80,
        minWidth: 110,
        disableSortBy: true,
        Cell: ({ row }) => {
          return <BaseCell>{formatDate(row.original.created)}</BaseCell>
        },
      },
      {
        ...generateDeleteButtonColumn<CustomerAttributesColumn>({
          onClick: () => setIsConfirmDeleteOpen(true),
          buttonLabel: 'Delete',
          hasTooltip: true,
          isVisibleOnlyOnHover: false,
          isDefaultColor: false,
        }),
        Header: 'Actions',
        width: 80,
        minWidth: 110,
        disableSortBy: true,
      },
    ],
    [isCollapsed]
  )
  return (
    <div className="sm:-ml-6 sm:-mr-6">
      {(customerAttributes?.data.length ?? 0) > 0 && (
        <Spinner isSpinning={isLoading || deleteQuery.isLoading}>
          <Table
            columns={columns}
            data={[{ id: 1, ...customerAttributes }]} // id is required for the actions (Delete button).
            className="mt-6 "
            rowClassName="bg-coolGray-50 "
          />
          {!isCollapsed && (
            <Table
              className="bg-coolGray-50 pt-2"
              columns={
                customerAttributes?.columns.map((value, index) => ({
                  Header: value,
                  accessor: value,
                  ...(index === 0
                    ? { width: 400, minWidth: 200, isResizable: true }
                    : undefined),
                })) ?? []
              }
              data={customerAttributes?.data ?? []}
              onSwapColumns={() => {}}
            />
          )}
        </Spinner>
      )}
      <ConfirmationModal
        title="Delete this file?"
        confirmationText="Are you sure you want to delete this file?"
        confirmButtonText="Delete file"
        open={isConfirmDeleteOpen}
        onCancel={() => setIsConfirmDeleteOpen(false)}
        onConfirm={() => {
          setIsConfirmDeleteOpen(false)
          deleteQuery.mutate({ accountId, baseUrl })
        }}
      />
    </div>
  )
}

export default CustomerAttributesTable
